import { yupResolver } from "@hookform/resolvers/yup";
import { SpinningButton } from "Components/ReactstrapSlim";
import { CheckboxControlled, InputFL, PasswordInputFL, ValidationSummary } from "Components/Inputs";
import React, { useEffect, useMemo, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useApiClient } from "Services/API/useApiClient";
import { AccountClient, RegisterSuccessResponseNormal, RegisterSuccessResponseTwoFactorConfirmationRequired } from "Services/LegacyAPI/AccountClient";
import * as yup from "yup";
import { SeparatorWithText } from "./SeparatorWithText";
import { SigningEmailProps, useSigningEmailEffect } from "./SigningCommon";
import { SsoButtons, SsoRegistrationOptions } from "./SsoButtons";

export type ExtendedRegisterSuccessResponse = RegisterSuccessResponseNormal | ExtendedRegisterSuccessResponseTwoFactorConfirmationRequired

interface ExtendedRegisterSuccessResponseTwoFactorConfirmationRequired extends RegisterSuccessResponseTwoFactorConfirmationRequired {
   email: string;
   password: string;
}

interface RegisterFormModel {
   FullName: string,
   Email: string,
   Password: string,
   RememberMe: boolean,
   AcceptTerms: boolean
}

const termsValidationMessage = "The terms must be accepted.";

const RegisterFormModelSchema: yup.SchemaOf<RegisterFormModel> = yup.object({
   FullName: yup.string().fullName(true, "Full name is required").defined(),
   Email: yup.string()
      .required("Email is required")
      .email("Email is not valid")
      .defined(),
   Password: yup.string()
      .required("Password is required")
      .min(LiquidSpaceConfig.NewMinPasswordLength, "Password must be at least ${min} characters long")
      .max(LiquidSpaceConfig.MaxPasswordLength, "Enter no more than ${max} characters")
      .passwordStrength()
      .test("no-spaces", "Password shouldn't contain spaces", (value) =>
         !value || !value.includes(" "))
      .defined(),
   RememberMe: yup.boolean().required(),
   AcceptTerms: yup.boolean().required(termsValidationMessage)
      .oneOf([true], termsValidationMessage)
}).defined();


const TermsLabel: React.FC = () => {
   return (<>
      By signing up  you are giving the thumbs up to our <a href="/terms/terms-of-use" target="_blank">Terms of Service</a> and <a href="/terms/privacy-policy" target="_blank">Privacy Policy</a>.
   </>);
}

export interface RegisterFormProps extends SigningEmailProps {
   isActive: boolean;
   onRegisterSuccess: (registerSuccessResponse: ExtendedRegisterSuccessResponse) => boolean;
   onSwitchToLogin: () => void;
   registerProps?: LSStore.SigningRegisterProps;
   registerModel?: LSWebUI.Models.RegisterModel;
}

export const RegisterForm: React.FC<RegisterFormProps> = function ({ isActive, onRegisterSuccess, onSwitchToLogin, registerProps, registerModel, ...signingEmailProps }) {
   const accountClient = useApiClient(AccountClient);
   const [isLoading, setIsLoading] = useState<boolean>(false);
   const [serverErrors, setServerErrors] = useState<Array<[string, string]>>([]);

   const formMethods = useForm<RegisterFormModel>({
      resolver: yupResolver(RegisterFormModelSchema),
      defaultValues: {
         FullName: registerProps?.FullName || undefined,
         Email: signingEmailProps?.email,
         RememberMe: true,
         AcceptTerms: true
      },
      mode: "onTouched"
   });

   useSigningEmailEffect(signingEmailProps, formMethods, "Email");

   const onSubmit = async () => {
      if (isLoading)
         return;
      setIsLoading(true);
      setServerErrors([]);
      let finalIsLoading = false;
      try {
         const isValid = await formMethods.trigger();
         if (isValid) {
            const formValues = formMethods.getValues();
            const registerRequest: Partial<LSWebUI.Models.RegisterModel> = {
               ...registerProps,
               ...registerModel,
               ...formValues
            };

            const requestResult = await accountClient.register(registerRequest);

            if (requestResult.success) {
               if (requestResult.showTwoFactorForm) {
                  finalIsLoading = onRegisterSuccess({
                     ...requestResult,
                     email: formValues.Email,
                     password: formValues.Password
                  });
               }
               else {
                  finalIsLoading = onRegisterSuccess(requestResult);
               }
            } else {
               if (requestResult.errors && requestResult.errors.length)
                  setServerErrors(requestResult.errors.map(err => ["email", err]));
               else
                  setServerErrors([["email", "Unexpected server error"]]);
            }
         }
         else {
            let name: keyof FieldErrors<RegisterFormModel>;
            for (name in formMethods.formState.errors) {
               formMethods.setFocus(name);
               break;
            }
         }
      } catch (err: any) {
         if (err.error) {
            setServerErrors([["email", err.error]]);
         }
         console.log(err);
      } finally {
         setIsLoading(finalIsLoading);
      }
   }

   const submitOnEnterHandler = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
         onSubmit();
      }
   }

   const onLoginClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onSwitchToLogin();
   }

   const ssoRegistrationOptions = useMemo<SsoRegistrationOptions | undefined>(() => {
      if (registerModel) {
         const result: SsoRegistrationOptions = {
            RegistrationType: registerModel.RegistrationType,
            MemberIdToLink: registerModel.MemberId != Guid.Empty ? registerModel.MemberId : undefined,
            OriginatingVenueId: registerModel.OriginatingVenueId,
            Email: registerModel.Email ?? undefined,
            InvitationId: registerModel.InvitationId ?? undefined,
            InvitationOwnerId: registerModel.InvitationOwnerId ?? undefined,
            OwnerId: registerModel.OwnerId ?? undefined,
            ConnectionType: registerModel.ConnectionType ?? undefined,
         };
         return result;
      }
      else if (registerProps) {
         return {
            RegistrationType: registerProps.RegistrationType || LSCommon.Enums.RegistrationType.Direct,
            ConnectionType: registerProps.ConnectionType
         }
      }
      else {
         return undefined;
      }
   }, [registerModel])

   useEffect(() => {
      if (isActive) {
         setTimeout(function () {
            formMethods.setFocus("FullName");
         }, 500);
      }
   }, [isActive])

   useEffect(() => {
      if (registerProps?.FullName && !formMethods.getValues("FullName"))
         formMethods.setValue("FullName", registerProps.FullName);
   }, [registerProps])

   return (
      <div className="signing-mode-inner">
         <div className="signing-header" aria-label="Sign up">
            Sign up
         </div>
         {(registerProps?.Message) && <div className="signing-caption">{registerProps?.Message}</div>}

         <div className="signing-register-form">
            <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverErrors} className="text-center" />
            <InputFL control={formMethods.control} name="FullName" placeholder="Full name" aria-label="full name" className="srf-input" onKeyDown={submitOnEnterHandler} />
            <InputFL control={formMethods.control} name="Email" placeholder="Email" aria-label="email" className="srf-input" autoComplete="off" onKeyDown={submitOnEnterHandler} />
            <PasswordInputFL control={formMethods.control} name="Password" placeholder="Password" aria-label="password" className="srf-input" autoComplete="off" onKeyDown={submitOnEnterHandler} />
            <CheckboxControlled control={formMethods.control} name="RememberMe" label="Stay signed in" aria-label="stay signed in" className="srf-checkbox" />
            <CheckboxControlled control={formMethods.control} name="AcceptTerms" label={<TermsLabel />} aria-label="accept terms of use" className="srf-checkbox" />
            <SpinningButton isLoading={isLoading} onClick={onSubmit} color="primary" size="md" fluid className="srf-cta">Sign up</SpinningButton>
            <div className="signing-mode-switcher">Do you have an account? <a href="#" onClick={onLoginClick}>Log in</a></div>
            <SeparatorWithText text="OR" />
            <SsoButtons ssoRegistrationOptions={ssoRegistrationOptions} size="md" />
         </div>
      </div>
   )
}